// Die Schulterkamera (Taste V): Sie steht drei Blöcke schräg hinter dem Avatar und blickt in
// dieselbe Richtung wie in der Ego-Sicht, damit sich die Steuerung nicht ändert
// (tasks/task-011, Abnahmekriterium „V wechselt zur Schulterkamera und zurück“).
//
// Reine Rechnung ohne Three.js: Aus dem Augenpunkt wird der Standort der Kamera. „Schräg“ heißt,
// dass die Rückwärtsrichtung um einen kleinen Winkel um die Hochachse gedreht ist – die Kamera
// blickt über die rechte Schulter, die Figur steht links vom Fadenkreuz und verdeckt es nicht.
// Steckt hinter dem Avatar eine Wand, rückt die Kamera davor, statt in ihr zu stecken.

import type { ViewPose } from './camera.js';

export const SCHULTER = {
  /** Abstand der Kamera vom Augenpunkt in Blöcken. */
  abstand: 3,
  /** Seitliche Drehung der Rückwärtsrichtung im Bogenmaß; positiv heißt über die rechte Schulter. */
  seitenwinkel: 0.15,
  /** So dicht darf die Kamera dem Auge kommen; enger sähe man von innen in den eigenen Kopf. */
  mindestabstand: 0.4,
  /** Sicherheitsabstand zur Wand, damit die Kamera nicht auf der Blockgrenze sitzt. */
  rand: 0.15,
  /** So viele Punkte werden auf dem Weg nach hinten auf feste Blöcke geprüft. */
  schritte: 24,
} as const;

/** Ob der Block an dieser Blockkoordinate fest ist; im Spiel `isSolid(world, …)`. */
export type IstFest = (x: number, y: number, z: number) => boolean;

/**
 * Der Standort der Schulterkamera zu einem Augenpunkt. Blickrichtung (`yaw`, `pitch`) bleibt
 * unverändert; nur die Position wandert nach hinten.
 */
export function schulterPose(pose: ViewPose, istFest: IstFest): ViewPose {
  const zurueck = rueckrichtung(pose.yaw + SCHULTER.seitenwinkel, pose.pitch);
  const abstand = Math.max(SCHULTER.mindestabstand, freierAbstand(pose, zurueck, istFest));
  return {
    x: pose.x + zurueck.x * abstand,
    y: pose.y + zurueck.y * abstand,
    z: pose.z + zurueck.z * abstand,
    yaw: pose.yaw,
    pitch: pose.pitch,
  };
}

/** Die Gegenrichtung zum Blick als Einheitsvektor: yaw 0 blickt nach −z, positiver pitch nach oben. */
function rueckrichtung(yaw: number, pitch: number): { x: number; y: number; z: number } {
  const waagerecht = Math.cos(pitch);
  return { x: Math.sin(yaw) * waagerecht, y: -Math.sin(pitch), z: Math.cos(yaw) * waagerecht };
}

/**
 * Wie weit hinter dem Auge der Weg frei ist. Geprüft werden gleichmäßige Punkte auf der Strecke,
 * jeweils um `rand` weiter draußen; sobald einer in einem festen Block liegt, gilt der Punkt davor.
 */
function freierAbstand(pose: ViewPose, zurueck: { x: number; y: number; z: number }, istFest: IstFest): number {
  for (let i = 1; i <= SCHULTER.schritte; i++) {
    const abstand = (SCHULTER.abstand * i) / SCHULTER.schritte;
    const weit = abstand + SCHULTER.rand;
    const x = Math.floor(pose.x + zurueck.x * weit);
    const y = Math.floor(pose.y + zurueck.y * weit);
    const z = Math.floor(pose.z + zurueck.z * weit);
    if (istFest(x, y, z)) return (SCHULTER.abstand * (i - 1)) / SCHULTER.schritte;
  }
  return SCHULTER.abstand;
}
